import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api } from "../../lib/api";
import { Menu, X, ChevronDown } from "lucide-react";
import LangSwitcher from "./LangSwitcher";

const LOGO = "https://customer-assets.emergentagent.com/job_277e14a4-0f30-4a88-8c2a-65bd1a744cb8/artifacts/o7xpulnt_logo-horizontal-cecit-for-light-bg%20%282%29.png";

/**
 * Apply CMS menu_config to a groups list.
 *   config = { hidden: [keys], order: [flat list of all keys] }
 * Any top-level group or sub-item whose `key` is in `hidden` is dropped.
 * The `order` array is a flat list containing both top-level group keys and
 * sub-item keys mixed together, so we sort each level independently by the
 * first occurrence of its key in that array.
 */
function applyMenuConfig(groups, config) {
  const hidden = new Set(config?.hidden || []);
  const order = config?.order || [];
  const rank = new Map(order.map((k, i) => [k, i]));
  const rankOf = (k, fallbackIdx) => (rank.has(k) ? rank.get(k) : 10000 + fallbackIdx);
  const filtered = groups
    .filter((g) => !hidden.has(g.key))
    .map((g) => ({
      ...g,
      items: g.items
        .filter((it) => !hidden.has(it.key || it.to))
        .map((it, idx) => ({ ...it, __rank: rankOf(it.key, idx) }))
        .sort((a, b) => a.__rank - b.__rank),
    }));
  if (!order.length) return filtered;
  return [...filtered]
    .map((g, idx) => ({ ...g, __rank: rankOf(g.key, idx) }))
    .sort((a, b) => a.__rank - b.__rank);
}

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [menuConfig, setMenuConfig] = useState({ hidden: [], order: [] });
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setMenuConfig(r.data?.menu_config?.header || { hidden: [], order: [] });
    }).catch(() => {});
  }, []);

  useEffect(() => { setOpen(false); setOpenMenu(null); }, [location.pathname]);

  const rawGroups = [
    { key: "camara", label: t("nav.camara"), items: [
      { key: "camara.overview", to: "/la-camara", label: t("nav.camaraOverview") },
      { key: "camara.mision", to: "/mision-objetivos", label: t("nav.mision") },
      { key: "camara.contacto", to: "/contacto", label: t("nav.contacto") },
    ]},
    { key: "servicios", label: t("nav.servicios"), items: [
      { key: "servicios.info", to: "/servicios/informacion-empresarial", label: t("services.info") },
      { key: "servicios.training", to: "/servicios/plataforma-formacion", label: t("services.training") },
      { key: "servicios.visa", to: "/servicios/gestion-visados", label: t("services.visa") },
      { key: "servicios.jobs", to: "/servicios/bolsa-empleo", label: t("services.jobs") },
      { key: "servicios.opportunities", to: "/servicios/oportunidades-negocio", label: t("services.opportunities") },
    ]},
    { key: "informacion", label: t("nav.informacion"), items: [
      { key: "informacion.anuario", to: "/anuario", label: t("nav.anuario") },
      { key: "informacion.newsletters", to: "/newsletters", label: t("nav.newsletters") },
      { key: "informacion.noticias", to: "/noticias-socios", label: t("nav.noticias") },
      { key: "informacion.invertir", to: "/invertir-marruecos", label: t("nav.invertir") },
    ]},
    { key: "actividades", label: t("nav.actividades"), items: [
      { key: "actividades.all", to: "/actividades", label: t("activityCategories.all") },
      { key: "actividades.proximas", to: "/actividades/proximas", label: t("activityCategories.proximas") },
      { key: "actividades.encuentros", to: "/actividades?cat=encuentros", label: t("activityCategories.encuentros") },
      { key: "actividades.seminarios", to: "/actividades?cat=seminarios", label: t("activityCategories.seminarios") },
      { key: "actividades.salones_ferias", to: "/actividades?cat=salones_ferias", label: t("activityCategories.salones_ferias") },
      { key: "actividades.webinars", to: "/actividades?cat=webinars", label: t("activityCategories.webinars") },
      { key: "actividades.visitas_reuniones", to: "/actividades?cat=visitas_reuniones", label: t("activityCategories.visitas_reuniones") },
      { key: "actividades.actos_institucionales", to: "/actividades?cat=actos_institucionales", label: t("activityCategories.actos_institucionales") },
      { key: "actividades.jornadas_academicas", to: "/actividades?cat=jornadas_academicas", label: t("activityCategories.jornadas_academicas") },
    ]},
    { key: "socios", label: t("nav.socios"), items: [
      { key: "socios.ventajas", to: "/ventajas-socio", label: t("nav.ventajas") },
      { key: "socios.standard", to: "/socios-standard", label: t("nav.sociosStandard") },
      { key: "socios.premium", to: "/socios-premium", label: t("nav.sociosPremium") },
      { key: "socios.buscador", to: "/buscador-socios", label: t("nav.buscador") },
    ]},
  ];
  const groups = applyMenuConfig(rawGroups, menuConfig);

  return (
    <>
      {/* Top bar */}
      <div className="hidden lg:block bg-[#0F2040] text-white text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center">
          <span className="tracking-wider opacity-80">
            {t("header.topbar")}
          </span>
          <div className="flex items-center gap-4">
            <a href="mailto:buzon.oficial@camacoestanger.com" className="hover:text-cecit-gold transition-colors">buzon.oficial@camacoestanger.com</a>
            <span className="opacity-30">|</span>
            <LangSwitcher testId="lang-toggle-top" />
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-xl shadow-sm" : "bg-white"} border-b border-gray-100`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="header-logo" className="flex items-center gap-3">
              <img src={LOGO} alt="CECIT" className="h-12 w-auto" />
            </Link>

            <nav className="hidden xl:flex items-center gap-1">
              <NavLink to="/" end className={({isActive}) => `px-3 py-2 text-sm font-medium tracking-wide transition-colors ${isActive ? "text-[#C8102E]" : "text-[#0F2040] hover:text-[#C8102E]"}`}>
                {t("nav.home")}
              </NavLink>
              {groups.map((g) => (
                <div
                  key={g.key}
                  className="relative"
                  onMouseEnter={() => setOpenMenu(g.key)}
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  <button
                    data-testid={`nav-${g.key}`}
                    className="px-3 py-2 text-sm font-medium tracking-wide text-[#0F2040] hover:text-[#C8102E] transition-colors flex items-center gap-1"
                  >
                    {g.label}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {openMenu === g.key && (
                    <div className="absolute top-full left-0 pt-2 min-w-[260px]">
                      <div className="bg-white border border-gray-100 shadow-xl py-2">
                        {g.items.map((it) => (
                          <Link
                            key={it.to}
                            to={it.to}
                            data-testid={`nav-link-${it.to.replace(/\//g, "-")}`}
                            className="block px-5 py-2.5 text-sm text-[#0F2040] hover:bg-gray-50 hover:text-[#C8102E] transition-colors"
                          >
                            {it.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <div className="hidden lg:block">
                <LangSwitcher testId="lang-toggle-header" />
              </div>
              <Link
                to="/ventajas-socio"
                data-testid="header-cta-member"
                className="hidden sm:inline-flex bg-[#C8102E] hover:bg-[#A30D25] text-white text-sm font-medium px-5 py-2.5 transition-colors"
              >
                {t("nav.becomeMember")}
              </Link>
              <button
                onClick={() => setOpen(!open)}
                data-testid="mobile-menu-toggle"
                className="xl:hidden text-[#0F2040] p-2"
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="xl:hidden bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 space-y-1">
              <Link to="/" className="block py-2.5 text-[#0F2040] font-medium border-b border-gray-100">{t("nav.home")}</Link>
              {groups.map((g) => (
                <details key={g.key} className="border-b border-gray-100">
                  <summary className="py-2.5 text-[#0F2040] font-medium cursor-pointer flex items-center justify-between">
                    {g.label}
                    <ChevronDown className="w-4 h-4" />
                  </summary>
                  <div className="pl-4 pb-2 space-y-1">
                    {g.items.map((it) => (
                      <Link key={it.to} to={it.to} className="block py-2 text-sm text-gray-700 hover:text-[#C8102E]">
                        {it.label}
                      </Link>
                    ))}
                  </div>
                </details>
              ))}
              <div className="pt-4 flex gap-3 items-center">
                <div className="flex-1">
                  <LangSwitcher testId="lang-toggle-mobile" align="left" />
                </div>
                <Link to="/ventajas-socio" className="flex-1 py-3 text-sm font-semibold bg-[#C8102E] text-white text-center">
                  {t("nav.becomeMember")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
